import type { GitSidebarCategory } from '@readme/api/src/mappings/sidebar/types';
import type { NextPageLink } from '@readme/backend/models/page/types';

import React, { useMemo } from 'react';

import useReadmeApi from '@core/hooks/useReadmeApi';
import useUniqueId from '@core/hooks/useUniqueId';
import { useProjectStore, useSuperHubStore } from '@core/store';

import { RHFGroup } from '@ui/RHF';
import WhatsNext from '@ui/WhatsNext';

import { useSuperHubEditorFormContext } from '../../Context';

import { flattenGitSidebarPagesToMongo, normalizeMongoPagesToGit, normalizeGitPagesToMongo } from './util';

export default function WhatsNextEditor() {
  const uid = useUniqueId('WhatsNextEditor');
  const whatsNextLabel = useProjectStore(s => s.data.appearance.whats_next_label);
  const [getApiBaseUrl, defaultApiOptions] = useSuperHubStore(s => [s.getApiBaseUrl, s.defaultApiOptions]);
  const { control } = useSuperHubEditorFormContext();

  const { data: guidesSidebarData } = useReadmeApi<GitSidebarCategory[]>(
    `${getApiBaseUrl()}/sidebar?page_type=guide`,
    defaultApiOptions,
  );
  const { data: referenceSidebarData } = useReadmeApi<GitSidebarCategory[]>(
    `${getApiBaseUrl()}/sidebar?page_type=reference`,
    defaultApiOptions,
  );

  /**
   * Flat list of all guides AND reference pages to allow users to pick from.
   */
  const internalPages = useMemo(() => {
    const mergedData = [...(guidesSidebarData ?? []), ...(referenceSidebarData ?? [])];
    return mergedData.reduce<NextPageLink[]>((result, category) => {
      return [...result, ...flattenGitSidebarPagesToMongo(category.pages, category.title)];
    }, []);
  }, [guidesSidebarData, referenceSidebarData]);

  return (
    <RHFGroup control={control} id={uid('content-next')} name="content.next">
      {({ field }) => (
        <WhatsNext
          description={field.value?.description ?? undefined}
          internalPages={internalPages}
          isEditable
          label={whatsNextLabel ?? undefined}
          onChange={event => {
            field.onChange({
              ...field.value,
              description: event.target.value,
            });
          }}
          onPagesUpdate={nextPages => {
            field.onChange({
              ...field.value,
              pages: normalizeMongoPagesToGit(nextPages),
            });
          }}
          pages={normalizeGitPagesToMongo(field.value?.pages ?? [])}
        />
      )}
    </RHFGroup>
  );
}

export * from './util';
